/**
 * Dashboard Redux Slice
 * 
 * This slice manages the dashboard state including:
 * - Row 2 filters (curriculum/grade/classroom)
 * - Row 4 date range (startDate/endDate) - SEPARATE from Row 2
 * - Dashboard summary data (activeStudents, activeTeachers, activeStaff, etc.)
 * - Student attendance data (present, absent, leave, pending)
 * 
 * Note: Async thunks are defined in dashboard.action.ts (separate file)
 * 
 * @module dashboard.slice
 */

import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import i18n from '../../i18n/i18n';
import {
  IDashboardState,
  IDashboardFilters,
  IAttendanceDateRange,
  IDashboardSummary,
  IAttendanceSummary,
} from '../../interface/dashboard.interface';
import { fetchDashboardSummary, fetchStudentAttendance } from '../action';

// ==================== Initial State ====================

/**
 * Initial state for dashboard slice
 * All filters start as null (no filtering applied)
 */
const initialState: IDashboardState = {
  // UI State
  isLoadingSummary: false,
  isLoadingAttendance: false,
  error: null,

  // Row 2 Filters (curriculum/grade/classroom)
  filters: {
    curriculumId: null,
    gradeId: null,
    classroomId: null,
  },

  // Row 4 Date Range (INDEPENDENT from Row 2 filters)
  attendanceDateRange: {
    startDate: null,
    endDate: null,
  },

  // Data
  summary: null,
  studentAttendance: null,

  // Cache timestamps (5-minute TTL)
  summaryLastFetched: null,
  attendanceLastFetched: null,
};

// ==================== Slice Definition ====================

const dashboardSlice = createSlice({
  name: 'dashboard',
  initialState,
  reducers: {
    /**
     * Set Row 2 filters (curriculum/grade/classroom)
     * Affects: Active Students, Pending Invoices, Upcoming Events
     * Does NOT affect: Active Teachers, Active Staff, Student Attendance
     */
    setFilters: (state, action: PayloadAction<Partial<IDashboardFilters>>) => {
      state.filters = {
        ...state.filters,
        ...action.payload,
      };
    },

    /**
     * Clear all Row 2 filters (reset to initial state)
     */
    clearFilters: (state) => {
      state.filters = {
        curriculumId: null,
        gradeId: null,
        classroomId: null,
      };
    },

    /**
     * Set Row 4 attendance date range
     * Affects ONLY student attendance data (Row 4)
     */
    setAttendanceDateRange: (state, action: PayloadAction<Partial<IAttendanceDateRange>>) => {
      state.attendanceDateRange = {
        ...state.attendanceDateRange,
        ...action.payload,
      };
    },

    /**
     * Clear Row 4 attendance date range (reset to initial state)
     */
    clearAttendanceDateRange: (state) => {
      state.attendanceDateRange = {
        startDate: null,
        endDate: null,
      };
    },

    /**
     * Reset entire dashboard state to initial state
     * Useful for cleanup on unmount or logout
     */
    resetDashboardState: (state) => {
      return initialState;
    },

    /**
     * Clear error message
     */
    clearError: (state) => {
      state.error = null;
    },

    /**
     * Manually update summary data (for optimistic updates)
     */
    setSummary: (state, action: PayloadAction<IDashboardSummary>) => {
      state.summary = action.payload;
      state.summaryLastFetched = Date.now();
    },

    /**
     * Manually update attendance data (for optimistic updates)
     */
    setStudentAttendance: (state, action: PayloadAction<IAttendanceSummary>) => {
      state.studentAttendance = action.payload;
      state.attendanceLastFetched = Date.now();
    },
  },

  // ==================== Extra Reducers (Async Thunk Handlers) ====================

  extraReducers: (builder) => {
    // ========== fetchDashboardSummary (Row 2/3 data) ==========

    builder.addCase(fetchDashboardSummary.pending, (state) => {
      state.isLoadingSummary = true;
      state.error = null;
    });

    builder.addCase(fetchDashboardSummary.fulfilled, (state, action) => {
      state.isLoadingSummary = false;
      if (action.payload?.data) {
        state.summary = action.payload.data.summary;
        state.summaryLastFetched = Date.now();
      }
    });

    builder.addCase(fetchDashboardSummary.rejected, (state, action) => {
      state.isLoadingSummary = false;
      state.error = action.error.message || i18n.t('toast.failed_to_fetch_dashboard_summary', 'Failed to fetch dashboard summary');
    });

    // ========== fetchStudentAttendance (Row 4 data) ==========

    builder.addCase(fetchStudentAttendance.pending, (state) => {
      state.isLoadingAttendance = true;
      state.error = null;
    });

    builder.addCase(fetchStudentAttendance.fulfilled, (state, action) => {
      state.isLoadingAttendance = false;
      if (action.payload?.data?.studentAttendance) {
        state.studentAttendance = action.payload.data.studentAttendance;
        state.attendanceLastFetched = Date.now();
      }
    });

    builder.addCase(fetchStudentAttendance.rejected, (state, action) => {
      state.isLoadingAttendance = false;
      state.error = action.error.message || i18n.t('toast.failed_to_fetch_student_attendance', 'Failed to fetch student attendance');
    });
  },
});

// ==================== Exports ====================

// Export actions
export const {
  setFilters,
  clearFilters,
  setAttendanceDateRange,
  clearAttendanceDateRange,
  resetDashboardState,
  clearError,
  setSummary,
  setStudentAttendance,
} = dashboardSlice.actions;

// Export reducer
export default dashboardSlice.reducer;

// ==================== Selectors (Optional - for easy state access) ====================

/**
 * Select Row 2 filters state
 */
export const selectFilters = (state: { dashboard: IDashboardState }) => state.dashboard.filters;

/**
 * Select Row 4 date range state
 */
export const selectAttendanceDateRange = (state: { dashboard: IDashboardState }) =>
  state.dashboard.attendanceDateRange;

/**
 * Select dashboard summary data
 */
export const selectSummary = (state: { dashboard: IDashboardState }) => state.dashboard.summary;

/**
 * Select student attendance data
 */
export const selectStudentAttendance = (state: { dashboard: IDashboardState }) =>
  state.dashboard.studentAttendance;

/**
 * Select loading states
 */
export const selectLoadingStates = (state: { dashboard: IDashboardState }) => ({
  isLoadingSummary: state.dashboard.isLoadingSummary,
  isLoadingAttendance: state.dashboard.isLoadingAttendance,
});

/**
 * Select error message
 */
export const selectError = (state: { dashboard: IDashboardState }) => state.dashboard.error;

/**
 * Check if cache is still valid (5-minute TTL)
 */
export const selectIsSummaryCacheValid = (state: { dashboard: IDashboardState }) => {
  const { summaryLastFetched } = state.dashboard;
  if (!summaryLastFetched) return false;
  const CACHE_TTL = 5 * 60 * 1000; // 5 minutes
  return Date.now() - summaryLastFetched < CACHE_TTL;
};

/**
 * Check if attendance cache is still valid (5-minute TTL)
 */
export const selectIsAttendanceCacheValid = (state: { dashboard: IDashboardState }) => {
  const { attendanceLastFetched } = state.dashboard;
  if (!attendanceLastFetched) return false;
  const CACHE_TTL = 5 * 60 * 1000; // 5 minutes
  return Date.now() - attendanceLastFetched < CACHE_TTL;
};
